


































































import { mapGetters, mapState } from 'vuex';
import * as vr from '@/helpers/validation';
import {
  SelectItem,
  TCopyStakeSettingsLimitsPayload,
  TCopyStakeSettingsLimitsResponse
} from '@/api/schema';
import { ECopyStakePackageProgram } from '@/api/schema';

import {
  getLimitsConfiguration,
  updateLimitsConfiguration
} from '@/api/CopyStake/Settings';
import { ECopyStakeFeatureFlag } from '@/store/modules/feature-flags/FeatureFlagNames';
import { cloneDeep } from 'lodash';
import { errorToastMessage } from '@/helpers/errorToastMessage';

type SettingsCopyStakePackageViewData = {
  limitsConfigSavedData: TCopyStakeSettingsLimitsResponse | null;
  loading: boolean;
  formData: {
    program: ECopyStakePackageProgram;
    streamersLimit: string;
    watchersLimit: string;
    webProgram: ECopyStakePackageProgram;
    webStreamersLimit: string;
    webWatchersLimit: string;
  };
  areLimitsFieldsEditable: boolean;
  areWebLimitsFieldsEditable: boolean;
  isFormValid: boolean;
};

export default {
  name: 'SettingsCopyStakePackageView',
  data(): SettingsCopyStakePackageViewData {
    return {
      loading: true,
      areLimitsFieldsEditable: true,
      areWebLimitsFieldsEditable: true,
      isFormValid: false,

      limitsConfigSavedData: null,

      formData: {
        program: ECopyStakePackageProgram.MINI_PROVISION,
        streamersLimit: '0',
        watchersLimit: '0',
        webProgram: ECopyStakePackageProgram.MINI_PROVISION,
        webStreamersLimit: '0',
        webWatchersLimit: '0'
      }
    };
  },
  computed: {
    ...mapState('SuperAdmin', ['operator']),
    ...mapGetters('FeatureFlags', ['isEnabledFeatureFlag']),
    isWebStreamersFeatureEnabled(): boolean {
      return this.isEnabledFeatureFlag(ECopyStakeFeatureFlag.WEB_STREAMERS);
    },
    isFormDisabled(): boolean {
      return this.loading;
    },
    rules(): Record<string, unknown[]> {
      return {
        streamersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        watchersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        webStreamersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        webWatchersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ]
      };
    },
    programs(): SelectItem[] {
      return [
        {
          text: 'Mini Provision',
          value: ECopyStakePackageProgram.MINI_PROVISION
        },
        {
          text: 'Standard Provision',
          value: ECopyStakePackageProgram.STANDARD_PROVISION
        },
        {
          text: 'Enterprise',
          value: ECopyStakePackageProgram.ENTERPRISE
        }
      ];
    },
    programsSettingMap(): Record<
      ECopyStakePackageProgram,
      {
        streamersLimit: number | null;
        watchersLimit: number | null;
        areLimitsFieldsEditable: boolean;
      }
    > {
      return {
        [ECopyStakePackageProgram.MINI_PROVISION]: {
          streamersLimit: 2,
          watchersLimit: 500,
          areLimitsFieldsEditable: false
        },
        [ECopyStakePackageProgram.STANDARD_PROVISION]: {
          streamersLimit: 5,
          watchersLimit: 2000,
          areLimitsFieldsEditable: false
        },
        [ECopyStakePackageProgram.ENTERPRISE]: {
          streamersLimit: null,
          watchersLimit: null,
          areLimitsFieldsEditable: true
        }
      };
    }
  },
  created(): void {
    this.getConfiguration();
  },
  watch: {
    operator: {
      handler(): void {
        this.getConfiguration();
      },
      deep: true
    },
    limitsConfigSavedData: {
      handler(): void {
        if (this.limitsConfigSavedData === null) {
          return;
        }

        this.setupFormData();
        this.setupWebFormData();

        this.$refs.form?.resetValidation();
      },
      deep: true
    },
    'formData.program': {
      handler(program: ECopyStakePackageProgram): void {
        const { streamersLimit, watchersLimit, areLimitsFieldsEditable } =
          this.programsSettingMap[program];

        this.formData.streamersLimit = `${
          streamersLimit ?? this.limitsConfigSavedData.streamers ?? 0
        }`;
        this.formData.watchersLimit = `${
          watchersLimit ?? this.limitsConfigSavedData.watchers ?? 0
        }`;
        this.areLimitsFieldsEditable = areLimitsFieldsEditable;

        this.$refs.form?.resetValidation();
      },
      deep: true
    },
    'formData.webProgram': {
      handler(webProgram: ECopyStakePackageProgram): void {
        const { streamersLimit, watchersLimit, areLimitsFieldsEditable } =
          this.programsSettingMap[webProgram];

        this.formData.webStreamersLimit = `${
          streamersLimit ?? this.limitsConfigSavedData.webStreamers ?? 0
        }`;
        this.formData.webWatchersLimit = `${
          watchersLimit ?? this.limitsConfigSavedData.webWatchers ?? 0
        }`;
        this.areWebLimitsFieldsEditable = areLimitsFieldsEditable;

        this.$refs.form?.resetValidation();
      },
      deep: true
    }
  },
  methods: {
    setupFormData(): void {
      if (
        this.limitsConfigSavedData.streamers === null ||
        this.limitsConfigSavedData.watchers === null
      ) {
        this.formData.program = ECopyStakePackageProgram.MINI_PROVISION;
        this.formData.streamersLimit = 2;
        this.formData.watchersLimit = 500;

        return;
      }

      this.formData.streamersLimit = `${
        this.limitsConfigSavedData.streamers ?? 0
      }`;
      this.formData.watchersLimit = `${
        this.limitsConfigSavedData.watchers ?? 0
      }`;
      this.formData.program = ECopyStakePackageProgram.ENTERPRISE;

      for (const program in this.programsSettingMap) {
        const { streamersLimit, watchersLimit } =
          this.programsSettingMap[program];

        if (
          this.limitsConfigSavedData.streamers === streamersLimit &&
          this.limitsConfigSavedData.watchers === watchersLimit
        ) {
          this.formData.program = program;

          break;
        }
      }
    },
    setupWebFormData(): void {
      if (
        this.limitsConfigSavedData.webStreamers === null ||
        this.limitsConfigSavedData.webWatchers === null
      ) {
        this.formData.webProgram = ECopyStakePackageProgram.MINI_PROVISION;
        this.formData.webStreamersLimit = 2;
        this.formData.webWatchersLimit = 500;

        return;
      }

      this.formData.webStreamersLimit = `${
        this.limitsConfigSavedData.webStreamers ?? 0
      }`;
      this.formData.webWatchersLimit = `${
        this.limitsConfigSavedData.webWatchers ?? 0
      }`;
      this.formData.webProgram = ECopyStakePackageProgram.ENTERPRISE;

      for (const program in this.programsSettingMap) {
        const { streamersLimit, watchersLimit } =
          this.programsSettingMap[program];

        if (
          this.limitsConfigSavedData.webStreamers === streamersLimit &&
          this.limitsConfigSavedData.webWatchers === watchersLimit
        ) {
          this.formData.webProgram = program;

          break;
        }
      }
    },
    resetComponentState(): void {
      this.loading = true;
      this.limitsConfigSavedData = null;
      this.formData = {
        program: ECopyStakePackageProgram.MINI_PROVISION,
        streamersLimit: '2',
        watchersLimit: '500',
        webProgram: ECopyStakePackageProgram.MINI_PROVISION,
        webStreamersLimit: '2',
        webWatchersLimit: '500'
      };
      this.areLimitsFieldsEditable = true;
      this.areWebLimitsFieldsEditable = true;
      this.isFormValid = false;
      this.$refs.form?.resetValidation();
    },
    async getConfiguration(): Promise<void> {
      try {
        this.resetComponentState();

        await getLimitsConfiguration()
          .then((data: TCopyStakeSettingsLimitsResponse) => {
            this.limitsConfigSavedData = cloneDeep(data);
          })
          .finally(() => {
            this.loading = false;
          });
      } catch (error) {
        this.$toast.error('Something was wrong', error);
      }
    },
    async submitHandler(): Promise<void> {
      if (this.isFormDisabled) {
        return;
      }

      const form = this.$refs.form;
      form.validate();

      await this.$nextTick();

      if (!this.isFormValid) {
        return;
      }

      this.loading = true;

      const payload: TCopyStakeSettingsLimitsPayload = cloneDeep(
        this.limitsConfigSavedData
      );

      payload.streamers = +this.formData.streamersLimit;
      payload.watchers = +this.formData.watchersLimit;

      if (this.isWebStreamersFeatureEnabled) {
        payload.webStreamers = +this.formData.webStreamersLimit;
        payload.webWatchers = +this.formData.webWatchersLimit;
      }

      await updateLimitsConfiguration(payload)
        .then((data: TCopyStakeSettingsLimitsResponse) => {
          this.resetComponentState();
          this.limitsConfigSavedData = cloneDeep(data);

          this.$toast.success('CopyStake packages successfully updated!');
        })
        .catch(errorToastMessage)
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
